import { useEffect, useState } from 'react';
import { Box, Card, Chip, Divider, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import {
  ChatOutlined,
  QuestionAnswer as QuestionAnswerIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { SLOT_LIST_LEFT_COLUMN_MIN_WIDTH_PX } from './slot-list-layout';
import { SlotRightSectionPlayIcon, type SlotSegmentAnchor } from './slot-right-section-play-icon';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import { FlowControlSlotMediaBelowRow } from './flow-control-slot-media-below-row';
import { useFlowControlSegmentStatus } from './use-flow-control-segment-status';
import { FLOW_CONTROL_SLOT_LIST_GAP } from '../flow-detail.types';
import { FlowControlPlayColumnRail } from './flow-control-play-column-rail';
import { useFlowStore } from '@/state/stores/use-flow-store';
import {
  flowControlActiveSegmentBackground,
  flowControlIdleSegmentBackground,
  flowControlNextCueSegmentBackground,
  resolveFlowControlSegmentAccent,
} from '@/helpers/flow-control-segment-accent.helper';
import { useFlowControlPlayingSegmentExpandId } from './use-flow-control-playing-segment-expand-id';
import { useFlowControlNextCueSegmentExpandId } from './use-flow-control-next-cue-segment-expand-id';
import { flowControlSlotIdsEqual, isFlowControlNextCueSegmentRow } from '@/helpers/flow-control-playing-segment.helper';
import { isFlowControlInBetweenSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import type { CueSheetRundownInBetween } from '@/helpers/group-cue-sheet-rundown.helper';
import { useConfidenceControlQuery, type ConfidenceControl } from '@/state/queries/player/use-confidence';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { ConfidenceRundownProvider, useConfidenceRundown } from './confidence-rundown-provider';
import { SESSION_TYPE } from '@/types/dtos/sessions/session-type.enum';
import { qaIconColor, qaNotice, resolveSectionQa } from './confidence-qa.helper';

type FlowControlPlayoutSegmentRowProps = {
  label: string;
  segmentAnchor: SlotSegmentAnchor;
  segmentKind: FlowControlSegmentKind;
  segmentId: string;
  slotId?: string;
  isExpanded: boolean;
  isActive?: boolean;
  /** When true, shows stop chrome but does not send stop (rare; overlay/list default allows stop). */
  disableManualStop?: boolean;
  /** When false, playlist panel is rendered by the parent (e.g. narrow overlay). Default true. */
  showMediaBelow?: boolean;
  /** Timed session chrome (slot.normal.*) instead of pre/after-flow playout fill. */
  normalSlotChrome?: boolean;
  /** Effective-flow slot for session/break inline media (omit for flow-level sections). */
  slot?: FlowSlotDto | null;
  /** Clock start (HH:mm). Empty when the section has no planned time. */
  startTime?: string;
  /** Planned/computed duration label (e.g. `20 min`). */
  durationLabel?: string;
  /** Reserve the SlotList time column so rundown titles stay aligned. */
  showTimingColumn?: boolean;
  /** Transition/resume playlist rendered after this section's media. */
  trailingInBetween?: CueSheetRundownInBetween;
  /** Every playlist item is skipped — same mark as a media row's skip icon. */
  showSkippedPlaylist?: boolean;
  onToggleExpand: () => void;
};

/** Control tab: 32px playout segment row with optional time/duration column and expandable playlists. */
export function FlowControlPlayoutSegmentRow(props: FlowControlPlayoutSegmentRowProps) {
  const shared = useConfidenceRundown();
  if (shared) return <SegmentRow {...props} />;
  return (
    <ConfidenceRundownProvider>
      <SegmentRow {...props} />
    </ConfidenceRundownProvider>
  );
}

function SegmentRow({
  label,
  segmentAnchor,
  segmentKind,
  segmentId,
  slotId,
  isExpanded,
  isActive,
  disableManualStop = false,
  showMediaBelow = true,
  normalSlotChrome: normalSlotChromeProp,
  slot = null,
  startTime,
  durationLabel,
  showTimingColumn = false,
  trailingInBetween,
  showSkippedPlaylist = false,
  onToggleExpand,
}: FlowControlPlayoutSegmentRowProps) {
  const t = useTranslations();
  const theme = useTheme();
  const rundown = useConfidenceRundown();
  const isLiveContextRunning = useFlowStore((s) => Boolean(s.flow?.liveContext?.isRunning));
  const segmentAccent = resolveFlowControlSegmentAccent(isLiveContextRunning);
  const playingExpandId = useFlowControlPlayingSegmentExpandId();
  const nextCueExpandId = useFlowControlNextCueSegmentExpandId();
  const { isActive: hookSegmentPlaying, isPreloaded: isSegmentPreloaded } = useFlowControlSegmentStatus(
    segmentAnchor,
    segmentKind,
    slotId,
  );
  const isSegmentPlaying =
    isActive === true || flowControlSlotIdsEqual(playingExpandId, segmentId) || hookSegmentPlaying;
  const qa = rundown?.qa?.flow.enabled ? rundown.qa : null;
  // Look-ahead fill only when Next cue item lives in this segment's playlist.
  const isNextCueSegment = isFlowControlNextCueSegmentRow(segmentId, nextCueExpandId, isSegmentPlaying);
  const normalSlotChrome =
    normalSlotChromeProp ?? (segmentKind != null && isFlowControlInBetweenSegmentKind(segmentKind));
  const segmentBackground = isSegmentPlaying
    ? flowControlActiveSegmentBackground(theme, segmentAccent)
    : isNextCueSegment
      ? flowControlNextCueSegmentBackground(theme)
      : normalSlotChrome
        ? 'slot.normal.active'
        : flowControlIdleSegmentBackground(theme);
  // Playing / Next use fill only — colored outlines are reserved for media items.
  const showExpandOutline = isExpanded && !isSegmentPlaying && !isNextCueSegment;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: FLOW_CONTROL_SLOT_LIST_GAP }}>
      <Card
        elevation={0}
        data-slot-id={segmentId}
        data-flow-playout-segment={segmentAnchor}
        sx={{
          height: '32px',
          minHeight: '32px',
          borderRadius: normalSlotChrome ? undefined : 0,
          overflow: 'hidden',
          bgcolor: segmentBackground,
          outlineWidth: showExpandOutline ? '2px' : '1px',
          outlineStyle: 'solid',
          outlineColor: showExpandOutline ? 'secondary.main' : normalSlotChrome ? 'divider' : 'black',
          cursor: 'pointer',
          // Avoid 300ms tap delay / missed clicks next to the widened play rail on touch devices.
          touchAction: 'manipulation',
        }}
        onClick={onToggleExpand}
      >
        <Stack direction="row" alignItems="stretch" sx={{ height: '100%' }}>
          {showTimingColumn ? (
            <>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={{
                  minWidth: SLOT_LIST_LEFT_COLUMN_MIN_WIDTH_PX,
                  width: SLOT_LIST_LEFT_COLUMN_MIN_WIDTH_PX,
                  flexShrink: 0,
                  px: 0.5,
                }}
              >
                {startTime ? (
                  <Typography
                    variant="slotStartTime"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    {startTime}
                  </Typography>
                ) : null}
                {durationLabel ? (
                  <Typography
                    variant="slotDuration"
                    color={durationLabel.startsWith('0 ') ? 'warning' : 'text.secondary'}
                    sx={{
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                  >
                    {durationLabel}
                  </Typography>
                ) : null}
              </Stack>
              <Divider orientation="vertical" flexItem />
            </>
          ) : null}
          <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 0, px: 1 }}>
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                minWidth: 0,
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 500,
              }}
            >
              {label}
            </Typography>
            {showSkippedPlaylist ? (
              <Tooltip title={t('media.playlistAdvanceMode.skip' as TranslationKey)}>
                <VisibilityOffIcon
                  data-testid="rundown-section-skipped"
                  sx={{ fontSize: 18, color: 'info.main', flexShrink: 0, ml: 0.5 }}
                />
              </Tooltip>
            ) : null}
            {qa && rundown && isTalkSlot(slot) && !(rundown.hasConfidence && isSegmentPlaying) ? (
              <TalkQaButton slot={slot} qa={qa} label={label} onOpen={rundown.openQa} />
            ) : null}
            {rundown?.hasConfidence && isSegmentPlaying ? (
              <LiveConfidenceControls onOpen={(panel) => rundown.openControl({ panel, slotId, sectionTitle: label })} />
            ) : null}
          </Stack>
          <Stack sx={{ height: '100%', alignSelf: 'stretch' }}>
            <FlowControlPlayColumnRail onSpacerClick={onToggleExpand}>
              <SlotRightSectionPlayIcon
                segmentAnchor={segmentAnchor}
                segmentKind={segmentKind}
                slotId={slotId}
                disableManualStop={disableManualStop}
                segmentActiveOverride={isSegmentPlaying}
                onActivate={onToggleExpand}
                ready={isSegmentPreloaded}
                segmentTitle={label}
              />
            </FlowControlPlayColumnRail>
          </Stack>
        </Stack>
      </Card>
      {showMediaBelow && isExpanded ? (
        <FlowControlSlotMediaBelowRow
          slot={slot ?? null}
          activeSlotId={segmentId}
          show
          trailingInBetween={trailingInBetween}
        />
      ) : null}
    </Box>
  );
}

function isTalkSlot(slot: FlowSlotDto | null): slot is FlowSlotDto<'Session'> {
  return Boolean(slot && slot.contentType === 'Session' && slot.content?.type === SESSION_TYPE.TALK);
}

function TalkQaButton({
  slot,
  qa,
  label,
  onOpen,
}: {
  slot: FlowSlotDto<'Session'>;
  qa: { flow: { enabled: boolean; minutes: number }; overrides: Record<string, { enabled: boolean; minutes: number }> };
  label: string;
  onOpen: (request: { slotId: string; originId?: string; sectionTitle: string; fullMinutes?: number }) => void;
}) {
  const t = useTranslations();
  const choice = resolveSectionQa(qa.flow, qa.overrides, slot.id, slot.origin);
  const fullMinutes =
    slot.computedDurationMinutes ?? slot.plannedDurationMinutes ?? slot.content?.plannedDurationMinutes;
  const notice = qaNotice(choice, fullMinutes);
  const noticeText =
    notice === 'error'
      ? t('flows.detail.control.confidence.qaNone' as TranslationKey)
      : notice === 'warning'
        ? t('flows.detail.control.confidence.qaShort' as TranslationKey)
        : '';
  return (
    <Tooltip title={noticeText}>
      <IconButton
        size="small"
        aria-label={t('flows.detail.control.confidence.qaAria' as TranslationKey)}
        onClick={(event) => {
          event.stopPropagation();
          onOpen({ slotId: slot.id, originId: slot.origin, sectionTitle: label, fullMinutes });
        }}
        sx={{ p: 0.25, gap: 0.25, color: qaIconColor(choice, fullMinutes) }}
      >
        <QuestionAnswerIcon sx={{ fontSize: 16 }} />
        <Box component="span" sx={{ fontSize: 11, fontWeight: 650, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {`${choice.enabled ? choice.minutes : 0}'`}
        </Box>
      </IconButton>
    </Tooltip>
  );
}

function LiveConfidenceControls({ onOpen }: { onOpen: (panel: 'time' | 'message') => void }) {
  const t = useTranslations();
  const rundown = useConfidenceRundown();
  const { data: confidence, dataUpdatedAt } = useConfidenceControlQuery(
    rundown?.entityId ? { entityType: 'Flow', entityId: rundown.entityId } : undefined,
  );

  return (
    <>
      {confidence?.timer ? (
        <ConfidenceTimerChip timer={confidence.timer} fetchedAt={dataUpdatedAt} onOpen={() => onOpen('time')} />
      ) : null}
      <IconButton
        size="small"
        aria-label={t('flows.detail.control.confidence.message' as TranslationKey)}
        onClick={(event) => {
          event.stopPropagation();
          onOpen('message');
        }}
        sx={{ p: 0.25 }}
      >
        <ChatOutlined sx={{ fontSize: 16 }} />
      </IconButton>
    </>
  );
}

type ConfidenceTimer = NonNullable<ConfidenceControl['timer']>;
type TimerPhase = 'plenty' | 'wrapUp' | 'final' | 'overtime';

const TIMER_PHASE_COLOR: Record<TimerPhase, string> = {
  plenty: '#3DDC97',
  wrapUp: '#F5C518',
  final: '#FF5A5A',
  overtime: '#FF5A5A',
};

function ConfidenceTimerChip({
  timer,
  fetchedAt,
  onOpen,
}: {
  timer: ConfidenceTimer;
  fetchedAt: number;
  onOpen: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);
  const remaining = confidenceRemainingSec(timer, now, fetchedAt);
  if (remaining == null) return null;
  const phase = confidenceTimerPhase(remaining, timer.wrapUpMinutes, timer.finalMinutes);
  return (
    <Chip
      size="small"
      clickable
      label={formatConfidenceRemaining(remaining)}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      sx={{
        'height': 20,
        'ml': 0.5,
        'mr': 1,
        'bgcolor': TIMER_PHASE_COLOR[phase],
        'color': '#1a1a1a',
        '& .MuiChip-label': { fontVariantNumeric: 'tabular-nums', fontWeight: 650 },
      }}
    />
  );
}

function confidenceRemainingSec(timer: ConfidenceTimer, nowMs: number, fetchedAt: number): number | null {
  const wallEnd = plannedInstantMs(timer.plannedEndAt, timer.plannedEndDate, timer.plannedEndClock);
  if (timer.basis === 'plannedWallClock' && wallEnd != null) return (wallEnd - nowMs) / 1000;
  if (timer.startedAt && timer.durationSec != null) {
    const skewMs = Number.isFinite(Date.parse(timer.serverNow)) ? Date.parse(timer.serverNow) - fetchedAt : 0;
    const end = Date.parse(timer.startedAt) + timer.durationSec * 1000;
    return (end - (nowMs + skewMs)) / 1000 - (timer.elapsedSec ?? 0);
  }
  if (timer.durationSec != null) return timer.durationSec;
  return null;
}

function plannedInstantMs(
  iso: string | undefined,
  dateYmd: string | undefined,
  clock: string | undefined,
): number | undefined {
  const exact = iso ? Date.parse(iso) : NaN;
  if (Number.isFinite(exact)) return exact;
  return localClockInstant(dateYmd, clock);
}

function localClockInstant(dateYmd: string | undefined, clock: string | undefined): number | undefined {
  if (!clock) return undefined;
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock.trim());
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return undefined;
  const base = dateYmd ? dateYmd.split('-').map(Number) : [];
  const now = new Date();
  const year = base[0] || now.getFullYear();
  const month = (base[1] || now.getMonth() + 1) - 1;
  const day = base[2] || now.getDate();
  return new Date(year, month, day, hours, minutes, 0, 0).getTime();
}

function confidenceTimerPhase(remainingSec: number, wrapUpMinutes: number, finalMinutes: number): TimerPhase {
  if (remainingSec < 0) return 'overtime';
  if (remainingSec <= finalMinutes * 60) return 'final';
  if (remainingSec <= wrapUpMinutes * 60) return 'wrapUp';
  return 'plenty';
}

function formatConfidenceRemaining(remainingSec: number): string {
  const negative = remainingSec < 0;
  const total = Math.abs(Math.round(remainingSec));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const body =
    hours > 0
      ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return negative ? `-${body}` : body;
}
